import styled, { css } from 'styled-components';
import COLORS from '../../style/universal.color';
import { TeamLabelProps } from './team.types';
import { MLBTheme } from '../../style/theme.types';
import Icon from '../Icon/icon';
import { IconProps } from '../Icon/icon.types';

const { mlbWhite, mlbGrey1, mlbGrey4, mlbGrey5, mlbGrey7, linkBlueOnDark, linkBlue, favoriteYellow } = COLORS;

interface OuterWrapperProps {
  recordBelow: boolean;
  leftAligned: boolean;
  dark: boolean;
  displayRecord: boolean;
  [dataAttrs: string]: object | string | boolean;
}

// The overall wrapper for the record component
export const OuterWrapper = styled.div.attrs<OuterWrapperProps>({
  'data-mlb-test': 'teamRecordWrapper',
  'data-test-id': 'teamRecordWrapper',
})`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  margin-left: ${({ leftAligned }) => (leftAligned ? '3px' : '0px')};
  img {
    margin-top: ${({ recordBelow }) => (recordBelow ? '3' : '0')}px;
    ${({ displayRecord }) => (!displayRecord ? `margin-top: 0;` : '')}
  }
  a,
  > div {
    display: flex;
    flex-direction: ${({ leftAligned }) => (leftAligned ? 'row' : 'row-reverse')};
  }
  &:hover {
    a {
      div:nth-child(2) {
        > div:first-child {
          border-bottom: 1px dotted ${({ dark }) => (dark ? linkBlueOnDark : linkBlue)};
        }
      }
    }
  }
`;

interface DataWrapperProps {
  recordBelow: boolean;
  leftAligned: boolean;
  displayRecord: boolean;
  displayStanding: boolean;
  fontSize: number;
  logoSize: number;
  theme: MLBTheme;
}

// The wrapper for the data (everything but logo) in the component
export const DataWrapper = styled.div<DataWrapperProps>`
  margin-right: ${({ leftAligned }) => (leftAligned ? '0' : '10')}px;
  margin-left: ${({ leftAligned }) => (leftAligned ? '10' : '0')}px;
  font-family: ${({ theme = {} }) => theme.fonts?.family?.primary};
  padding-top: ${({ recordBelow }) => (recordBelow ? '0' : '4')}px;
  ${({ displayRecord, fontSize, logoSize }) => (!displayRecord ? `padding-top: ${(logoSize - fontSize) / 2}px;` : '')}
  display: inline-block;
  vertical-align: middle;
  display: ${({ recordBelow }) => !recordBelow && 'inline-flex'};
  flex-direction: ${({ leftAligned }) => (leftAligned ? 'row' : 'row-reverse')};
`;

interface NameWrapperProps {
  recordBelow: boolean;
  leftAligned: boolean;
  dark: boolean;
  fontSize: number;
  isFavorite?: boolean;
  'data-mlb-test'?: string;
  'data-testid'?: string;
}

// The wrapper for the team name in the component
export const NameWrapper = styled.div.attrs<NameWrapperProps>({
  'data-mlb-test': 'teamNameLabel',
  'data-testid': 'teamNameLabel',
})`
  display: ${({ recordBelow }) => (recordBelow ? 'flex' : 'inline-flex')};
  flex-direction: ${({ leftAligned, isFavorite }) => (leftAligned && isFavorite ? 'row-reverse' : 'row')};
  align-items: center;
  margin-right: ${({ leftAligned }) => (leftAligned ? '4' : '0')}px;
  margin-left: ${({ leftAligned }) => (leftAligned ? '0' : '4')}px;
  margin-top: 1px;
  font-size: ${({ fontSize }) => fontSize}px;
  font-weight: bold;
  vertical-align: middle;
  text-align: ${({ leftAligned }) => (leftAligned ? 'left' : 'right')};
  box-sizing: content-box;
  border-bottom: 1px solid transparent;
  line-height: ${({ fontSize }) => fontSize}px;

  color: ${({ dark }) => (dark ? mlbGrey1 : mlbGrey7)};
`;

interface RecordWrapperProps {
  recordBelow: boolean;
  fontSize: number;
}

const TeamLabel = styled.div<TeamLabelProps>`
  font-size: ${({ fontSize }) => fontSize}px;
  line-height: ${({ fontSize }) => fontSize}px;
  text-align: ${({ leftAligned }) => (leftAligned ? 'left' : 'right')};
`;

// The wrapper for the record stats
export const DesktopRecordWrapper = styled(TeamLabel)<RecordWrapperProps>`
  display: ${({ recordBelow }) => (recordBelow ? 'block' : 'inline-block')};
  margin-top: ${({ recordBelow }) => (recordBelow ? '1px' : '6px')};
  color: ${mlbGrey5};
`;

export const MobileRecordWrapper = styled(DesktopRecordWrapper)`
  font-size: ${({ fontSize }) => fontSize - 1}px;
`;

export const DesktopStandingsWrapper = styled(TeamLabel)<RecordWrapperProps>`
  display: ${({ recordBelow }) => (recordBelow ? 'block' : 'inline-block')};
  margin-top: ${({ recordBelow }) => (recordBelow ? '1px' : '6px')};
  color: ${mlbGrey5};
  display: ${({ recordBelow }) => (recordBelow ? 'block' : 'none')};
`;

export const MobileStandingsWrapper = styled(DesktopRecordWrapper)`
  font-size: ${({ fontSize }: TeamLabelProps) => fontSize && fontSize - 1}px;
  display: ${({ recordBelow }: RecordWrapperProps) => (recordBelow ? 'block' : 'none')};
`;

interface SplitSquadWrapperProps {
  recordBelow?: boolean;
  leftAligned?: boolean;
  'data-mlb-test'?: string;
}

// TODO: Make the width and height more dynamic for
// the split squad identifier
export const splitSquadCoreStyle = css<SplitSquadWrapperProps>`
  box-sizing: content-box;
  text-align: center;
  color: ${mlbWhite};
  background-color: ${mlbGrey4};
  border: 1px solid ${mlbWhite};
  font-weight: bold;
  font-family: ${({ theme }) => theme.fonts?.family?.primary};
  border-radius: 6px;
  height: 7px;
  width: 12px;
  font-size: 7px;
  line-height: 8px;
`;

// Positions Split Squad notation in bottom right corner of logo
export const SplitSquadWrapper = styled(TeamLabel).attrs<SplitSquadWrapperProps>({
  'data-mlb-test': 'splitSquadLabel',
})`
  ${splitSquadCoreStyle}
  position: absolute;
  ${({ leftAligned }) => (leftAligned ? 'left: -10px;' : 'right: 18px;')}

  bottom: ${({ recordBelow }) => (recordBelow ? '6px' : '1px')};
`;

interface FavoriteIconWrapperProps extends Partial<IconProps> {
  width?: number;
  leftAligned: boolean;
}

export const FavoriteIconWrapper = styled(Icon).attrs<FavoriteIconWrapperProps>({
  name: 'star',
  fill: favoriteYellow,
})`
  ${({ leftAligned }) => (leftAligned ? 'margin-left' : 'margin-right')}: 3px;
  width: 0.75em;
`;

interface FavIconContainerProps {
  $width: number;
  $favIconWidth?: number;
  $favIconMargin?: number;
  $recordBelow: boolean;
}

export const FavIconContainer = styled.div<FavIconContainerProps>`
  position: absolute;
  left: 0;
  top: 0;
  width: ${({ $width }) => $width}px;
  height: ${({ $width }) => $width}px;

  svg {
    width: ${({ $favIconWidth }) => $favIconWidth}px;
    height: ${({ $favIconWidth }) => $favIconWidth}px;
    margin-top: ${({ $favIconMargin = 0, $recordBelow }) => $favIconMargin * 1 + ($recordBelow ? 3 : 0)}px;
    margin-left: ${({ $favIconMargin }) => $favIconMargin}px;
  }
`;
